'use client';

import {
  Button,
  FormControl,
  FormLabel,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { AuthResponse, IdentityProvidersNames } from '@repo/api-gw-sdk';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { FormError } from '@/components/form/formError';
import { FormTextField } from '@/components/form/formTextField';
import {
  emailValidation,
  isValidEmail,
} from '@/components/form/validation/emailValidation';
import { EonMark } from '@/components/layout/shared/eonMark';
import { Link } from '@/components/shared/link';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useNonProjectDal } from '@/contexts/useNonProjectDal';
import { getApiErrorMessage } from '@/utils/apiError';

const safeReturnUrl = (raw: string | null): string => {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) {
    return '/home';
  }
  return raw;
};

const NEW_PASSWORD_REQUIRED = 'NEW_PASSWORD_REQUIRED';

interface Challenge {
  session: string;
  email: string;
}

export const LoginV2Page = ({
  idpData,
}: {
  idpData: IdentityProvidersNames;
}) => {
  const { nonProjectDal: dal } = useNonProjectDal();
  const { isDev } = useEnvironment();
  const searchParams = useSearchParams();
  const returnUrl = safeReturnUrl(searchParams.get('returnUrl'));
  const domain = searchParams.get('domain') ?? undefined;

  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const ssoProviders = idpData.providers ?? [];

  const handleAuthResponse = (res: AuthResponse, email: string) => {
    if (res.ssoRedirect) {
      window.location.replace(res.ssoRedirect);
      return;
    }
    if (res.challengeName === NEW_PASSWORD_REQUIRED) {
      if (!res.session) {
        setError('Login could not continue. Please try again.');
        return;
      }
      setChallenge({ session: res.session, email });
      return;
    }
    if (res.challengeName) {
      setError(
        'This account requires a sign-in method that isn’t supported here.'
      );
      return;
    }
    window.location.replace(returnUrl);
  };

  const runAuth = async (
    email: string,
    call: () => Promise<AuthResponse>,
    fallbackError: string
  ) => {
    setError(null);
    setSubmitting(true);
    try {
      handleAuthResponse(await call(), email);
    } catch (e) {
      setError(getApiErrorMessage(e, fallbackError));
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isDev) {
      return;
    }
    dal.authV2
      .login({ email: '', password: '' }, domain)
      .then((res) => {
        if (!res.ssoRedirect && !res.challengeName) {
          window.location.replace(returnUrl);
        }
      })
      .catch(() => {
        // Leave the login form usable if the dev auto-login fails.
      });
  }, [isDev, domain, dal, returnUrl]);

  if (idpData.supportsSsoOnly) {
    return <SsoStep providers={ssoProviders} />;
  }

  if (challenge) {
    return (
      <NewPasswordStep
        error={error}
        submitting={submitting}
        onSubmit={(newPassword) =>
          void runAuth(
            challenge.email,
            () =>
              dal.authV2.respondChallenge(
                {
                  email: challenge.email,
                  challengeName: NEW_PASSWORD_REQUIRED,
                  session: challenge.session,
                  challengeResponses: { NEW_PASSWORD: newPassword },
                },
                domain
              ),
            'Could not set password'
          )
        }
      />
    );
  }

  return (
    <CredentialsStep
      error={error}
      submitting={submitting}
      ssoProviders={ssoProviders}
      onSubmit={(email, password) =>
        void runAuth(
          email,
          () => dal.authV2.login({ email, password }, domain),
          'Login failed'
        )
      }
    />
  );
};

const SsoStep = ({ providers }: { providers: string[] }) => {
  const { clientApiBaseUrl } = useEnvironment();

  return (
    <Stack
      sx={{
        width: '400px',
        maxWidth: '100%',
        margin: 'auto',
        alignItems: 'center',
        gap: '40px',
        paddingY: '40px',
      }}
    >
      <EonMark />
      <Typography variant='subtitle1' className='text-center'>
        Log In with SSO
      </Typography>
      {providers.length > 0 ? (
        <Stack sx={{ width: '100%', gap: '24px' }}>
          {providers.map((provider) => (
            <Button
              key={provider}
              component={Link}
              prefetch={false}
              href={`${clientApiBaseUrl}/saml/sso?provider=${encodeURIComponent(provider)}`}
              fullWidth
              variant='contained'
              sx={{ height: '48px' }}
            >
              {provider}
            </Button>
          ))}
        </Stack>
      ) : (
        <Typography
          variant='body1'
          className='text-center'
          data-testid='login-v2-no-sso-providers'
        >
          No SSO providers are configured.
        </Typography>
      )}
    </Stack>
  );
};

const SsoDropdown = ({ providers }: { providers: string[] }) => {
  const { clientApiBaseUrl } = useEnvironment();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  const ssoHref = (provider: string) =>
    `${clientApiBaseUrl}/saml/sso?provider=${encodeURIComponent(provider)}`;

  const [onlyProvider] = providers;
  if (providers.length === 1 && onlyProvider) {
    return (
      <Button
        component={Link}
        prefetch={false}
        href={ssoHref(onlyProvider)}
        fullWidth
        variant='contained'
        data-testid='login-v2-sso-button'
        sx={{ height: '48px' }}
      >
        Log In with SSO
      </Button>
    );
  }

  return (
    <>
      <Button
        fullWidth
        variant='contained'
        disabled={redirecting}
        data-testid='login-v2-sso-dropdown'
        onClick={(event) => setAnchorEl(event.currentTarget)}
        endIcon={<i className='material-symbols-arrow-drop-down-rounded' />}
        sx={{ height: '48px' }}
      >
        Log In with SSO
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        slotProps={{
          paper: { sx: { minWidth: `${anchorEl?.offsetWidth ?? 0}px` } },
        }}
      >
        {providers.map((provider) => (
          <MenuItem
            key={provider}
            component={Link}
            prefetch={false}
            href={ssoHref(provider)}
            onClick={() => {
              setAnchorEl(null);
              setRedirecting(true);
            }}
          >
            {provider}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const CredentialsStep = ({
  error,
  submitting,
  ssoProviders,
  onSubmit,
}: {
  error: string | null;
  submitting: boolean;
  ssoProviders: string[];
  onSubmit: (email: string, password: string) => void;
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const submit = () => {
    if (isValidEmail(email) && password) {
      onSubmit(email, password);
    }
  };

  return (
    <Stack
      sx={{
        width: '400px',
        maxWidth: '100%',
        margin: 'auto',
        alignItems: 'center',
        gap: '40px',
        paddingY: '40px',
      }}
    >
      <EonMark />
      {ssoProviders.length > 0 ? (
        <>
          <SsoDropdown providers={ssoProviders} />
          <Typography variant='subtitle1' className='text-center'>
            Or use your email account:
          </Typography>
        </>
      ) : (
        <Typography variant='subtitle1' className='text-center'>
          Login with your email account:
        </Typography>
      )}

      <Stack
        component='form'
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
        sx={{ width: '100%', gap: '40px' }}
      >
        <Stack sx={{ gap: '12px' }}>
          <FormTextField
            title='Email'
            placeholder='name@company.com'
            dataTestId='login-v2-email'
            validation={emailValidation}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ '& .MuiInputBase-input': { height: '36px' } }}
          />
          <FormControl size='small' className='w-full'>
            <FormLabel htmlFor='login-v2-password'>Password</FormLabel>
            <TextField
              type='password'
              autoComplete='current-password'
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ '& .MuiInputBase-input': { height: '36px' } }}
              slotProps={{
                htmlInput: {
                  id: 'login-v2-password',
                  'data-testid': 'login-v2-password',
                },
              }}
            />
          </FormControl>
        </Stack>

        <Stack direction='row' sx={{ gap: '24px', width: '100%' }}>
          <Button
            variant='text'
            component={Link}
            href={`/login-v2/forgot-password?email=${encodeURIComponent(email)}`}
            sx={{ flex: 1, height: '48px' }}
          >
            Forgot Password?
          </Button>
          <Button
            type='submit'
            variant='contained'
            disabled={!isValidEmail(email) || !password || submitting}
            data-testid='login-v2-submit'
            sx={{ flex: 1, height: '48px' }}
          >
            {submitting ? 'Signing in…' : 'Log In'}
          </Button>
        </Stack>
      </Stack>

      {error && <FormError message={error} testId='login-v2-error' />}
    </Stack>
  );
};

const NewPasswordStep = ({
  error,
  submitting,
  onSubmit,
}: {
  error: string | null;
  submitting: boolean;
  onSubmit: (newPassword: string) => void;
}) => {
  const [newPassword, setNewPassword] = useState('');

  return (
    <Stack
      sx={{
        width: '400px',
        maxWidth: '100%',
        margin: 'auto',
        alignItems: 'center',
        gap: '40px',
        paddingY: '40px',
      }}
    >
      <EonMark />
      <Typography variant='subtitle1' className='text-center'>
        Set a new password to continue:
      </Typography>

      <Stack
        component='form'
        onSubmit={(event) => {
          event.preventDefault();
          if (newPassword) {
            onSubmit(newPassword);
          }
        }}
        sx={{ width: '100%', gap: '40px' }}
      >
        <FormControl size='small' className='w-full'>
          <FormLabel htmlFor='login-v2-new-password'>New password</FormLabel>
          <TextField
            type='password'
            autoFocus
            autoComplete='new-password'
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            sx={{ '& .MuiInputBase-input': { height: '36px' } }}
            slotProps={{
              htmlInput: {
                id: 'login-v2-new-password',
                'data-testid': 'login-v2-new-password',
              },
            }}
          />
        </FormControl>

        <Button
          fullWidth
          type='submit'
          variant='contained'
          disabled={!newPassword || submitting}
          data-testid='login-v2-new-password-submit'
          sx={{ height: '48px' }}
        >
          {submitting ? 'Saving…' : 'Continue'}
        </Button>
      </Stack>

      {error && <FormError message={error} testId='login-v2-error' />}
    </Stack>
  );
};
